import { EndpointBuilder } from "@reduxjs/toolkit/query";
import { HTTP_METHODS } from "@src/constants";
import { Employee } from "@src/types";
import { DataConfirmationPayload } from "@src/types/notification_types";
import { DigixProject } from "@src/types/project_types";
import { DataConfirmationHistory } from "@src/types/resource_plan_types";
import {
  CreateRoleRequestPayload,
  RoleRequest,
  UpdateRoleRequestPayload,
} from "@src/types/role_request_types";
import {
  updateCachedRoleRequestByRequestId,
  updateCachedRoleRequestsByProjectId,
} from "@src/utils/cache_utils";
import { message } from "antd";
import { Promise } from "cypress/types/cy-bluebird";

import { baseApiSlice } from "./baseApiSlice";

export const projectsApi = baseApiSlice.injectEndpoints({
  endpoints: (builder: EndpointBuilder<any, any, any>) => ({
    /* GET endpoints */
    ...getEndpoints(builder),

    /* POST endpoints */
    ...postEndpoints(builder),

    /* PUT endpoints */
    ...putEndpoints(builder),
  }),
});

function getEndpoints(builder: EndpointBuilder<any, any, any>) {
  return {
    /**
     * Get role requests by project id
     */
    getRoleRequestsByProjectId: builder.query<RoleRequest[], string>({
      query: (projectId) => `/v2/projects/${projectId}/role-requests`,
      transformErrorResponse: async () => {
        message.error("Failed to get role requests by project id", 5);
      },
      providesTags: ["RoleRequests"],
    }),

    /**
     * Get project editors by project id
     */
    getProjectEditorsByProjectId: builder.query<Employee[], string>({
      query: (projectId) => `/v2/projects/${projectId}/editors`,
      transformErrorResponse: async () => {
        message.error("Failed to get project editors by project id", 5);
      },
      providesTags: ["ProjectEditors"],
    }),

    /**
     * Get projects by resource plan id
     */
    getProjectsByResourcePlanId: builder.query<DigixProject[], number>({
      query: (resourcePlanId) =>
        `/v2/projects?resourcePlanId=${resourcePlanId}`,
      transformErrorResponse: async () => {
        message.error("Failed to get projects by resource plan id", 5);
      },
      providesTags: ["Projects"],
    }),

    /**
     * Get all data confirmation history entries for a given resource plan id.
     */
    getConfirmationsByProjectId: builder.query<
      DataConfirmationHistory[],
      number
    >({
      query: (projectId) => `/v2/projects/${projectId}/confirmations`,
      transformErrorResponse: async () => {
        message.error("Failed to get resource plan confirmations", 5);
      },
      providesTags: ["DataConfirmation"],
    }),
  };
}

function postEndpoints(builder: EndpointBuilder<any, any, any>) {
  const baseMethod = HTTP_METHODS.POST;

  return {
    /**
     * Save role request by project id
     */
    saveRequestV2: builder.mutation<
      RoleRequest,
      {
        projectId: string;
        request: CreateRoleRequestPayload;
      }
    >({
      query: ({ projectId, request }) => ({
        url: `/v2/projects/${projectId}/role-requests`,
        method: baseMethod,
        body: request,
      }),
      transformErrorResponse: async () => {
        message.error("Error saving role request for project", 5);
      },
      invalidatesTags: ["ResourcePlan"],
    }),

    /**
     * Save data confirmation
     */
    saveDataConfirmation: builder.mutation<void, DataConfirmationPayload>({
      query: (request) => ({
        url: `/v2/projects/confirmations`,
        method: baseMethod,
        body: request,
      }),
      transformErrorResponse: async () => {
        message.error("Error saving data confirmation for project", 5);
      },
      invalidatesTags: ["DataConfirmation", "Notifications"],
    }),
  };
}

function putEndpoints(builder: EndpointBuilder<any, any, any>) {
  const baseMethod = HTTP_METHODS.PUT;

  return {
    /**
     * Update a role request for a project
     * @param projectId - project identifier
     * @param roleRequestId - role request identifier
     * @param roleRequest - role request
     */
    updateRoleRequest: builder.mutation<
      RoleRequest,
      {
        projectId: string;
        roleRequestId: string;
        requestPayload: UpdateRoleRequestPayload;
      }
    >({
      query: ({ projectId, roleRequestId, requestPayload }) => ({
        url: `/v2/projects/${projectId}/role-requests/${roleRequestId}`,
        method: baseMethod,
        body: requestPayload,
      }),
      transformErrorResponse: async () => {
        message.error("Error updating project role request", 5);
      },
      async onQueryStarted(
        { projectId, roleRequestId },
        { dispatch, queryFulfilled }
      ): Promise<void> {
        try {
          const { data: updatedRoleRequests } = await queryFulfilled;
          // updating the updated request in the list of requests which are found by the project id
          updateCachedRoleRequestsByProjectId(
            dispatch,
            projectId,
            updatedRoleRequests
          );
          // updating the single request which is fetch by the pl modal when opening
          updateCachedRoleRequestByRequestId(
            dispatch,
            parseInt(roleRequestId),
            updatedRoleRequests
          );
        } catch (e) {
          console.error(e);
        }
      },
      invalidatesTags: [
        "Requests",
        "TabNotifications",
        "TeamLeadRequest",
        "ResourcePlan",
      ],
    }),

    updateProjectEditors: builder.mutation<
      void,
      { projectId: string; projectEditorIds: number[] }
    >({
      query: ({ projectId, projectEditorIds }) => ({
        url: `/v2/projects/${projectId}/editors`,
        method: baseMethod,
        body: projectEditorIds,
      }),
      transformErrorResponse: async () => {
        message.error("Error updating project editors", 5);
      },
      invalidatesTags: ["ProjectEditors"],
    }),
  };
}

export const {
  /* GET endpoints */
  useGetRoleRequestsByProjectIdQuery,
  useGetProjectEditorsByProjectIdQuery,
  useGetProjectsByResourcePlanIdQuery,
  useGetConfirmationsByProjectIdQuery,

  /* POST endpoints */
  useSaveRequestV2Mutation,
  useSaveDataConfirmationMutation,

  /* PUT endpoints */
  useUpdateRoleRequestMutation,
  useUpdateProjectEditorsMutation,
} = projectsApi;
